import { create } from "zustand"
import {
    createTravelQuote,
    getTravelOffers,
    getTravelSubscription,
    patchTravelQuote,
    postTravelSubscription,
} from "@/app/endpoints/travelComparatorEndpoints"
import {
    ITravelOffer,
    ITravelPassenger,
    ITravelQuote,
    ITravelQuotesResponse,
    ITravelSubscriber,
    ITravelSubscriptionResponse,
    TravelTripType,
} from "@/app/models/travelComparator"
import { isTravelStep, TravelStep } from "@/app/components/travelComparator/travelFunnelSteps"
import { readClickIds } from "@/app/utils/travelAffiliate"

export type TravelMotif = "vacances" | "affaires" | "etudes" | "expatriation" | "working_holiday" | "volontariat"

const MOTIF_TO_TRIP_TYPE: Record<TravelMotif, TravelTripType> = {
    vacances: "single_trip",
    affaires: "single_trip",
    etudes: "long_stay",
    expatriation: "long_stay",
    working_holiday: "long_stay",
    volontariat: "long_stay",
}

// Motifs qui n'ont pas d'annulation à couvrir → on masque et on n'envoie pas le montant du voyage.
export const MOTIFS_WITHOUT_TRIP_AMOUNT: TravelMotif[] = ["volontariat"]

const TRIP_TYPE_TO_MOTIF: Partial<Record<TravelTripType, TravelMotif>> = {
    single_trip: "vacances",
    long_stay: "etudes",
}

// ProspectInfo::GENDERS — 0 = Monsieur, 1 = Madame.
const CIVILITY_TO_GENDER: Record<"MR" | "MS", number> = { MR: 0, MS: 1 }

const genderToCivility = (gender: number | null): "MR" | "MS" | null =>
    gender === 0 ? "MR" : gender === 1 ? "MS" : null

export interface TravelFormState {
    motif: TravelMotif
    residenceCountry: string
    destinationCountries: string[]
    startDate: string
    endDate: string
    // Prix du voyage par personne (€) — requis pour tarifer l'annulation (Chapka Cap Explorer…).
    tripAmount: string
    // Organisme d'accueil du volontariat/bénévolat — exigé par Chapka Cap Volontariat (motif volontariat).
    organisme: string
    adults: number
    travelers: { birthdate: string }[]
}

export type TravelSubscriber = ITravelSubscriber
export type TravelPassenger = ITravelPassenger

interface TravelComparatorState {
    token: string | null
    funnelStep: TravelStep
    hydrated: boolean
    form: TravelFormState
    result: ITravelQuotesResponse | null
    loadingOffers: boolean
    error: string | null
    selectedOffer: ITravelOffer | null
    subscriber: TravelSubscriber
    passengers: TravelPassenger[]
    consentAccepted: boolean
    eligibilityDeclared: boolean
    submitting: boolean
    subscriptionError: string | null
    subscriptionResult: ITravelSubscriptionResponse | null
    paid: boolean
    paymentPending: boolean
    hydrateFromQuote: (quote: ITravelQuote) => void
    patchForm: (patch: Partial<TravelFormState>) => void
    setAdults: (count: number) => void
    setTravelerBirthdate: (index: number, birthdate: string) => void
    startLandingComparison: () => Promise<string | null>
    persistSubscriberStep: () => Promise<void>
    persistPassengersStep: () => Promise<void>
    loadOffers: () => Promise<void>
    reset: () => void
    subscribe: (offer: ITravelOffer) => Promise<void>
    recordSelection: (offer: ITravelOffer) => Promise<void>
    patchSubscriber: (patch: Partial<TravelSubscriber>) => void
    setPassenger: (index: number, patch: Partial<TravelPassenger>) => void
    setConsent: (value: boolean) => void
    setEligibilityDeclared: (value: boolean) => void
    clearSubscriptionError: () => void
    pay: () => Promise<void>
    resumePaymentReturn: (subscriptionToken: string, status: "success" | "cancel") => Promise<void>
}

export const MAX_ADULTS = 20

// Mutable pour que les tests composant n'attendent pas les délais réels.
export const PAYMENT_RETURN_POLLING = { attempts: 8, delayMs: 2000 }

type PaymentOutcome = "settled" | "aborted" | "pending"

const paymentOutcome = (status: string): PaymentOutcome => {
    if (status === "paid" || status === "confirmed") {
        return "settled"
    }
    return status === "cancelled" || status === "failed" ? "aborted" : "pending"
}

// Chaque lecture serveur réconcilie le paiement chez le partenaire (webhook ou repli) : on relit jusqu'à décision.
async function pollPaymentReturn(subscriptionToken: string): Promise<ITravelSubscriptionResponse> {
    let response = await getTravelSubscription(subscriptionToken)
    const { attempts, delayMs } = PAYMENT_RETURN_POLLING
    for (let attempt = 1; attempt < attempts && paymentOutcome(response.status) === "pending"; attempt++) {
        await new Promise((resolve) => setTimeout(resolve, delayMs))
        response = await getTravelSubscription(subscriptionToken)
    }
    return response
}

const DEFAULT_FORM: TravelFormState = {
    motif: "vacances",
    residenceCountry: "FR",
    destinationCountries: [],
    startDate: "",
    endDate: "",
    tripAmount: "",
    organisme: "",
    adults: 1,
    travelers: [{ birthdate: "" }],
}

const DEFAULT_SUBSCRIBER: TravelSubscriber = {
    civility: null,
    lastName: "",
    firstName: "",
    birthdate: "",
    nationality: "FR",
    email: "",
    phone: "",
    address: "",
    city: "",
    zip: "",
}

function resizeTravelers(existing: { birthdate: string }[], total: number): { birthdate: string }[] {
    return Array.from({ length: total }, (_, index) => existing[index] ?? { birthdate: "" })
}

function buildTripPatch(form: TravelFormState) {
    const wantsTripAmount = !MOTIFS_WITHOUT_TRIP_AMOUNT.includes(form.motif)
    const tripAmount = wantsTripAmount ? Number(form.tripAmount) : 0
    return {
        tripType: MOTIF_TO_TRIP_TYPE[form.motif],
        tripMotif: form.motif,
        destinationCountries: form.destinationCountries,
        startDate: form.startDate,
        endDate: form.endDate,
        // options=["cancellation"] = annulation demandée (affichée au CRM via getOptions) ; le montant > 0
        // débloque les offres annulation Chapka (CAP-EXPLORER) et affine Ava/Heymondo.
        options: tripAmount > 0 ? ["cancellation"] : [],
        tripAmountPerPerson: tripAmount > 0 ? tripAmount : null,
        organisme: form.motif === "volontariat" ? form.organisme.trim() || null : null,
    }
}

function buildTravelersPatch(form: TravelFormState) {
    return {
        departureCountry: form.residenceCountry,
        residenceCountry: form.residenceCountry,
        travelers: form.travelers.map((traveler) => ({ birthdate: traveler.birthdate })),
    }
}

const isoDateOnly = (value: string | null | undefined): string => (value ? value.slice(0, 10) : "")

export const useTravelComparatorStore = create<TravelComparatorState>((set, get) => ({
    token: null,
    funnelStep: "offres",
    hydrated: false,
    form: DEFAULT_FORM,
    result: null,
    loadingOffers: false,
    error: null,
    selectedOffer: null,
    subscriber: DEFAULT_SUBSCRIBER,
    passengers: [],
    consentAccepted: false,
    eligibilityDeclared: false,
    submitting: false,
    subscriptionError: null,
    subscriptionResult: null,
    paid: false,
    paymentPending: false,

    // Rehydrates everything the server persisted (trip, travelers+names, lead, selected offer) so a
    // resumed token lands mid-subscription with its data; local typing wins when the server is empty.
    hydrateFromQuote: (quote) =>
        set((state) => {
            const travelers = (quote.travelers ?? []).map((traveler) => ({ birthdate: traveler.birthdate ?? "" }))
            const adults = Math.max(1, travelers.length)
            const subscriber = quote.subscriber
                ? {
                      ...state.subscriber,
                      civility: genderToCivility(quote.subscriber.gender) ?? state.subscriber.civility,
                      firstName: quote.subscriber.first_name ?? state.subscriber.firstName,
                      lastName: quote.subscriber.last_name ?? state.subscriber.lastName,
                      email: quote.subscriber.email ?? state.subscriber.email,
                      phone: quote.subscriber.phone ?? state.subscriber.phone,
                      birthdate: quote.subscriber.birthdate ?? state.subscriber.birthdate,
                      address: quote.subscriber.address ?? state.subscriber.address,
                      city: quote.subscriber.city ?? state.subscriber.city,
                      zip: quote.subscriber.zip ?? state.subscriber.zip,
                      nationality: quote.subscriber.nationality ?? state.subscriber.nationality,
                  }
                : state.subscriber
            const passengers = (quote.travelers ?? []).map((traveler, index) => ({
                firstName: traveler.first_name ?? state.passengers[index]?.firstName ?? "",
                lastName: traveler.last_name ?? state.passengers[index]?.lastName ?? "",
                birthdate: traveler.birthdate ?? state.passengers[index]?.birthdate ?? "",
                passport: traveler.passport ?? state.passengers[index]?.passport ?? "",
            }))

            return {
                token: quote.token,
                funnelStep: isTravelStep(quote.funnel_step) ? quote.funnel_step : "offres",
                hydrated: true,
                subscriber,
                passengers: passengers.length > 0 ? passengers : state.passengers,
                selectedOffer: quote.selected_offer ?? state.selectedOffer,
                form: {
                    ...state.form,
                    // Le motif persisté fait foi (plus fin que trip_type : études/expat/WHV = long_stay).
                    motif: (quote.trip_motif as TravelMotif | null) ?? TRIP_TYPE_TO_MOTIF[quote.trip_type] ?? state.form.motif,
                    residenceCountry: quote.residence_country ?? quote.departure_country ?? state.form.residenceCountry,
                    destinationCountries: quote.destination_countries ?? [],
                    startDate: isoDateOnly(quote.start_date),
                    endDate: isoDateOnly(quote.end_date),
                    tripAmount: quote.trip_amount_per_person != null ? String(quote.trip_amount_per_person) : "",
                    organisme: quote.organisme ?? "",
                    adults,
                    travelers: travelers.length > 0 ? travelers : [{ birthdate: "" }],
                },
            }
        }),

    patchForm: (patch) => set((state) => ({ form: { ...state.form, ...patch } })),

    setAdults: (count) =>
        set((state) => {
            const adults = Math.max(1, Math.min(MAX_ADULTS, count))
            return {
                form: { ...state.form, adults, travelers: resizeTravelers(state.form.travelers, adults) },
            }
        }),

    setTravelerBirthdate: (index, birthdate) =>
        set((state) => ({
            form: {
                ...state.form,
                travelers: state.form.travelers.map((traveler, i) => (i === index ? { birthdate } : traveler)),
            },
        })),

    // Tarification depuis la landing : réutilise le devis en cours (« Modifier ma simulation ») ou en
    // crée un, enregistre toute la simulation avec funnel_step=offres, puis renvoie le token pour
    // naviguer droit vers le loader + offres.
    startLandingComparison: async () => {
        set({ error: null })
        try {
            let token = get().token
            if (!token) {
                token = (await createTravelQuote()).token
                set({ token })
            }
            const { form } = get()
            await patchTravelQuote(token, {
                ...buildTripPatch(form),
                ...buildTravelersPatch(form),
                funnelStep: "offres",
            })
            set({ funnelStep: "offres" })
            return token
        } catch (error) {
            set({ error: error instanceof Error ? error.message : "Une erreur est survenue" })
            return null
        }
    },

    loadOffers: async () => {
        const { token } = get()
        if (!token) {
            return
        }
        set({ loadingOffers: true, error: null })
        try {
            const result = await getTravelOffers(token)
            set({ result, loadingOffers: false })
        } catch (error) {
            set({
                loadingOffers: false,
                error: error instanceof Error ? error.message : "Une erreur est survenue",
                result: { offers: [], partners: [] },
            })
        }
    },

    reset: () =>
        set({
            token: null,
            funnelStep: "offres",
            hydrated: false,
            form: DEFAULT_FORM,
            result: null,
            loadingOffers: false,
            error: null,
            selectedOffer: null,
            subscriber: DEFAULT_SUBSCRIBER,
            passengers: [],
            consentAccepted: false,
            eligibilityDeclared: false,
            submitting: false,
            subscriptionError: null,
            subscriptionResult: null,
            paid: false,
            paymentPending: false,
        }),

    // Sélection = FK serveur vers la ligne travel_offer du run ; le prix n'est jamais renvoyé par le client.
    subscribe: async (offer) => {
        set((state) => ({
            selectedOffer: offer,
            paid: false,
            paymentPending: false,
            consentAccepted: false,
            eligibilityDeclared: false,
            submitting: false,
            subscriptionError: null,
            subscriptionResult: null,
            funnelStep: "coordonnees",
            // Reprend la date de naissance de l'assuré principal (saisie à l'étape 2) sans écraser une saisie existante.
            subscriber: {
                ...state.subscriber,
                birthdate: state.subscriber.birthdate || (state.form.travelers[0]?.birthdate ?? ""),
            },
            passengers: state.form.travelers.map((traveler, index) => ({
                lastName: state.passengers[index]?.lastName ?? "",
                firstName: state.passengers[index]?.firstName ?? "",
                birthdate: state.passengers[index]?.birthdate || traveler.birthdate,
                passport: state.passengers[index]?.passport ?? "",
            })),
        }))

        const { token } = get()
        if (!token || typeof offer.id !== "number") {
            return
        }
        await patchTravelQuote(token, { selectedOffer: offer.id, funnelStep: "coordonnees", clickIds: readClickIds() }).catch((error) => console.error("travel quote patch failed", error))
    },

    // Offre affiliate : l'utilisateur part finaliser chez le partenaire (nouvel onglet), on ne passe donc
    // pas par notre étape "coordonnees". On enregistre quand même la sélection côté serveur (attribution
    // CRM / offre choisie) sans changer d'étape de tunnel. Fire-and-forget pour ne pas retarder la redirection.
    recordSelection: async (offer) => {
        set({ selectedOffer: offer })
        const { token } = get()
        if (!token || typeof offer.id !== "number") {
            return
        }
        await patchTravelQuote(token, { selectedOffer: offer.id, clickIds: readClickIds() }).catch((error) => console.error("travel quote patch failed", error))
    },

    persistSubscriberStep: async () => {
        const { token, subscriber } = get()
        if (!token) {
            return
        }
        set({ funnelStep: "details" })
        await patchTravelQuote(token, {
            subscriber: {
                gender: subscriber.civility ? CIVILITY_TO_GENDER[subscriber.civility] : null,
                firstName: subscriber.firstName,
                lastName: subscriber.lastName,
                phone: subscriber.phone,
                address: subscriber.address,
                city: subscriber.city,
                zip: subscriber.zip,
                nationality: subscriber.nationality,
                ...(subscriber.email ? { email: subscriber.email } : {}),
                ...(subscriber.birthdate ? { birthdate: subscriber.birthdate } : {}),
            },
            funnelStep: "details",
        }).catch((error) => console.error("travel quote patch failed", error))
    },

    persistPassengersStep: async () => {
        const { token, passengers } = get()
        if (!token) {
            return
        }
        set({ funnelStep: "paiement" })
        await patchTravelQuote(token, {
            passengers: passengers.map((passenger) => ({
                firstName: passenger.firstName,
                lastName: passenger.lastName,
                passport: passenger.passport || null,
                ...(passenger.birthdate ? { birthdate: passenger.birthdate } : {}),
            })),
            funnelStep: "paiement",
        }).catch((error) => console.error("travel quote patch failed", error))
    },

    patchSubscriber: (patch) => set((state) => ({ subscriber: { ...state.subscriber, ...patch } })),

    setPassenger: (index, patch) =>
        set((state) => ({
            passengers: state.passengers.map((passenger, i) => (i === index ? { ...passenger, ...patch } : passenger)),
        })),

    setConsent: (value) => set({ consentAccepted: value }),

    setEligibilityDeclared: (value) => set({ eligibilityDeclared: value }),

    clearSubscriptionError: () => set({ subscriptionError: null }),

    pay: async () => {
        const { token, selectedOffer, consentAccepted, eligibilityDeclared } = get()
        if (!token || !selectedOffer || !consentAccepted || !eligibilityDeclared) {
            return
        }

        set({ submitting: true, subscriptionError: null })

        try {
            // Le serveur fige offre/prix depuis la FK de sélection et lit lead + voyageurs sur la quote.
            const response = await postTravelSubscription({ consent: consentAccepted, quoteToken: token })

            // Lien de paiement partenaire à venir : s'il est présent on y redirige (PSP partenaire,
            // puis retour sur le parcours), sinon paiement simulé → succès.
            if (response.paymentUrl) {
                window.location.href = response.paymentUrl
                return
            }

            set({ paid: true, subscriptionResult: response, submitting: false })
        } catch (error) {
            set({
                submitting: false,
                subscriptionError: error instanceof Error ? error.message : "Une erreur est survenue",
            })
        }
    },

    resumePaymentReturn: async (subscriptionToken, status) => {
        if (status === "cancel") {
            set({
                submitting: false,
                paid: false,
                paymentPending: false,
                subscriptionError: "Paiement annulé. Vous pouvez réessayer.",
            })
            return
        }

        set({ submitting: true, paymentPending: false, subscriptionError: null })

        try {
            const response = await pollPaymentReturn(subscriptionToken)
            const outcome = paymentOutcome(response.status)
            set({
                submitting: false,
                subscriptionResult: response,
                paid: outcome === "settled",
                paymentPending: outcome === "pending",
                subscriptionError: outcome === "aborted" ? "Le paiement n'a pas abouti. Vous pouvez réessayer." : null,
            })
        } catch (error) {
            set({
                submitting: false,
                subscriptionError: error instanceof Error ? error.message : "Une erreur est survenue",
            })
        }
    },
}))
